import { useState, useEffect, useCallback } from 'react';
import MdLicenseBanner from "../components/MdLicenseBanner";
import {
  KeyRound, Plus, Loader2, Trash2, Pencil, Shield, Eye, EyeOff,
  Server, Wifi, Globe, Terminal, Lock, X, Save, HelpCircle,
  Star, FlaskConical, CheckCircle2, XCircle, Camera
} from 'lucide-react';
import { toast } from 'react-toastify';
import fetchWithTenant from '../utils/fetchWithTenant';
import useAuthStore from '../stores/authStore';
import useTutorialStore from '../stores/tutorialStore';
import useTutorialPrompt from '../hooks/useTutorialPrompt';
import TutorialPrompt from '../components/tutorials/TutorialPrompt';
import credentialsTutorial from '../components/tutorials/configs/credentialsTutorial';

const PROTOCOL_CONFIG = {
  SNMP_V2: {
    label: 'SNMP v2c', icon: <Wifi size={18} />, color: '#22c55e',
    fields: ['community'],
    description: 'Community string for SNMP v2c polling',
  },
  SNMP_V3: {
    label: 'SNMP v3', icon: <Shield size={18} />, color: '#10b981',
    fields: ['username', 'password', 'authProtocol', 'privProtocol', 'privPassword'],
    description: 'Authenticated & encrypted SNMP v3',
  },
  HTTP: {
    label: 'HTTP', icon: <Globe size={18} />, color: '#3b82f6',
    fields: ['username', 'password', 'endpoint'],
    description: 'HTTP Basic Auth for web interfaces',
  },
  ONVIF: {
    label: 'ONVIF', icon: <Camera size={18} />, color: '#06b6d4',
    fields: ['username', 'password'],
    description: 'ONVIF WSSE auth for direct camera access (audio/event analytics)',
  },
  WMI: {
    label: 'WMI', icon: <Server size={18} />, color: '#8b5cf6',
    fields: ['domain', 'username', 'password'],
    description: 'Windows Management Instrumentation',
  },
  SSH: {
    label: 'SSH', icon: <Terminal size={18} />, color: '#f59e0b',
    fields: ['username', 'password'],
    description: 'SSH credentials for Linux/network devices',
  },
  REDFISH: {
    label: 'Redfish / BMC', icon: <Server size={18} />, color: '#ef4444',
    fields: ['username', 'password'],
    description: 'iDRAC, iLO, XClarity, Supermicro BMC credentials',
  },
  API: {
    label: 'API Key', icon: <Lock size={18} />, color: '#ec4899',
    fields: ['apiHeader', 'apiKey', 'endpoint'],
    description: 'API key/token for REST health endpoints',
  },
};

const FIELD_LABELS = {
  username: 'Username', password: 'Password', community: 'Community String',
  authProtocol: 'Auth Protocol', privProtocol: 'Privacy Protocol',
  privPassword: 'Privacy Password', domain: 'Domain',
  apiKey: 'API Key / Token', apiHeader: 'Header Name', endpoint: 'Endpoint Path',
};

const SENSITIVE_FIELDS = ['username', 'password', 'community', 'privPassword', 'apiKey'];

const EMPTY_FORM = {
  label: '', protocol: 'SNMP_V2', description: '', priority: 0,
  credentialSubtype: '', isDefault: false,
  username: '', password: '', community: '', authProtocol: '',
  privProtocol: '', privPassword: '', domain: '',
  apiKey: '', apiHeader: '', endpoint: '',
};

const MdCredentials = ({ embedded = false }) => {
  const startTutorial = useTutorialStore((s) => s.startTutorial);
  const { showPrompt, dismissTutorial } = useTutorialPrompt(credentialsTutorial);

  const { tenantVersion } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [credentials, setCredentials] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [testModal, setTestModal] = useState(null); // { credentialId, label } | null
  const [testTargetIp, setTestTargetIp] = useState('');
  const [testRunning, setTestRunning] = useState(false);
  const [testResult, setTestResult] = useState(null); // { success, error? } | null


  // Reset on tenant switch
  useEffect(() => {
    setCredentials([]); setLoading(true);
  }, [tenantVersion]);

  const fetchCredentials = useCallback(async () => {
    try {
      const res = await fetchWithTenant('/api/md/credentials');
      const data = await res.json();
      if (data.success) setCredentials(data.data || []);
    } catch (err) {
      toast.error('Failed to load credentials');
    } finally {
      setLoading(false);
    }
  }, [tenantVersion]);

  useEffect(() => { fetchCredentials(); }, [fetchCredentials]);

  const openAdd = () => {
    setEditId(null);
    setForm({ ...EMPTY_FORM });
    setShowModal(true);
  };

  const openEdit = (cred) => {
    setEditId(cred.id);
    setForm({
      label: cred.label || '', protocol: cred.protocol || 'SNMP_V2',
      description: cred.description || '', priority: cred.priority || 0,
      credentialSubtype: cred.credentialSubtype || '',
      isDefault: !!cred.isDefault,
      username: cred.username || '', password: cred.password || '',
      community: cred.community || '', authProtocol: cred.authProtocol || '',
      privProtocol: cred.privProtocol || '', privPassword: cred.privPassword || '',
      domain: cred.domain || '', apiKey: cred.apiKey || '',
      apiHeader: cred.apiHeader || '', endpoint: cred.endpoint || '',
    });
    setShowModal(true);
  };

  const handleSetDefault = async (cred) => {
    try {
      const res = await fetchWithTenant(`/api/md/credentials/${cred.id}/default`, { method: 'PUT' });
      const data = await res.json();
      if (data.success) {
        toast.success(`"${cred.label}" is now default for ${cred.protocol}`);
        fetchCredentials();
      } else {
        toast.error(data.error || 'Failed to set default');
      }
    } catch (err) {
      toast.error('Failed to set default');
    }
  };

  const openTestModal = (cred) => {
    setTestModal({ credentialId: cred.id, label: cred.label });
    setTestTargetIp('');
    setTestResult(null);
  };

  const runTest = async () => {
    if (!testTargetIp.trim()) { toast.error('Target IP is required'); return; }
    setTestRunning(true);
    setTestResult(null);
    try {
      const res = await fetchWithTenant(`/api/md/credentials/${testModal.credentialId}/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetIp: testTargetIp.trim() }),
      });
      const data = await res.json();
      if (data.success && data.result?.success) {
        setTestResult({ success: true });
      } else {
        setTestResult({ success: false, error: data.result?.error || data.error || 'Test failed' });
      }
    } catch (err) {
      setTestResult({ success: false, error: err.message });
    } finally {
      setTestRunning(false);
    }
  };

  const handleSave = async () => {
    if (!form.label.trim()) { toast.error('Label is required'); return; }
    setSaving(true);
    try {
      const url = editId ? `/api/md/credentials/${editId}` : '/api/md/credentials';
      const method = editId ? 'PUT' : 'POST';
      const payload = {
        label: form.label, protocol: form.protocol,
        description: form.description, priority: form.priority,
        credentialSubtype: form.credentialSubtype || null,
        isDefault: !!form.isDefault,
      };
      const config = PROTOCOL_CONFIG[form.protocol];
      for (const field of config.fields) {
        if (form[field] && !form[field].includes('\u2022')) payload[field] = form[field];
      }
      const res = await fetchWithTenant(url, {
        method, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(editId ? 'Credential updated' : 'Credential added');
        setShowModal(false);
        fetchCredentials();
      } else {
        toast.error(data.error || 'Save failed');
      }
    } catch (err) {
      toast.error('Failed to save credential');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetchWithTenant(`/api/md/credentials/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        toast.success('Credential deleted');
        setDeleteConfirm(null);
        fetchCredentials();
      } else {
        toast.error(data.error || 'Failed to delete');
        setDeleteConfirm(null);
      }
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const togglePasswordVisibility = (credId, field) => {
    const key = `${credId}-${field}`;
    setShowPasswords(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const protocolConfig = PROTOCOL_CONFIG[form.protocol] || PROTOCOL_CONFIG.SNMP_V2;

  const grouped = credentials.reduce((acc, cred) => {
    const proto = cred.protocol || 'OTHER';
    if (!acc[proto]) acc[proto] = [];
    acc[proto].push(cred);
    return acc;
  }, {});

  return (
    <div className="page-content">
      <div className="page-header-with-actions">
        <div>
          <h1 className="page-title"><KeyRound size={28} /> Credential Bank</h1>
          <p className="page-subtitle">Credential Vault</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-primary" onClick={openAdd}>
            <Plus size={16} /> Add Credential
          </button>
          <button className="tutorial-help-btn" onClick={() => startTutorial(credentialsTutorial.module, credentialsTutorial.steps)} title="Take a tour" aria-label="Take a tour">
            <HelpCircle size={16} />
          </button>
        </div>
      </div>

      <MdLicenseBanner />

      <div className="md-cred-stats">
        {Object.entries(PROTOCOL_CONFIG).map(([key, config]) => {
          const count = grouped[key]?.length || 0;
          return (
            <div key={key} className="md-cred-stat" style={{ borderLeftColor: config.color }}>
              <div className="md-cred-stat-icon" style={{ color: config.color }}>{config.icon}</div>
              <div className="md-cred-stat-info">
                <span className="md-cred-stat-value">{count}</span>
                <span className="md-cred-stat-label">{config.label}</span>
              </div>
            </div>
          );
        })}
      </div>

      {loading ? (
        <div className="md-loading"><Loader2 size={24} className="spin" /> Loading credentials...</div>
      ) : credentials.length === 0 ? (
        <div className="md-empty-state">
          <KeyRound size={48} />
          <h3>No Credentials Configured</h3>
          <p>Add credentials to enable SNMP polling, HTTP checks, WMI monitoring, and more.</p>
          <button className="btn btn-primary" onClick={openAdd}>
            <Plus size={16} /> Add First Credential
          </button>
        </div>
      ) : (
        <div className="md-cred-list">
          {credentials.map((cred) => {
            const config = PROTOCOL_CONFIG[cred.protocol] || PROTOCOL_CONFIG.SNMP_V2;
            return (
              <div key={cred.id} className={`md-cred-card ${!cred.isEnabled ? 'disabled' : ''}`}>
                <div className="md-cred-card-header">
                  <div className="md-cred-card-icon" style={{ color: config.color, background: config.color + '18' }}>
                    {config.icon}
                  </div>
                  <div className="md-cred-card-title">
                    <span className="md-cred-card-label">{cred.label}</span>
                    <span className="md-cred-card-protocol" style={{ color: config.color }}>{config.label}</span>
                  </div>
                  {cred.isDefault && (
                    <span className="md-cred-default-badge" title={`Default ${config.label} credential`}>
                      <Star size={12} fill="currentColor" /> Default
                    </span>
                  )}
                  {!cred.isEnabled && <span className="md-cred-disabled-badge">Disabled</span>}
                  <div className="md-cred-card-actions">
                    <button
                      className={`md-action-btn ${cred.isDefault ? 'active' : ''}`}
                      onClick={() => !cred.isDefault && handleSetDefault(cred)}
                      disabled={cred.isDefault}
                      title={cred.isDefault ? 'Current default' : 'Set as default for this protocol'}
                    >
                      <Star size={14} fill={cred.isDefault ? 'currentColor' : 'none'} />
                    </button>
                    {cred.protocol === 'WMI' && (
                      <button className="md-action-btn" onClick={() => openTestModal(cred)} title="Test connection">
                        <FlaskConical size={14} />
                      </button>
                    )}
                    <button className="md-action-btn" onClick={() => openEdit(cred)} title="Edit">
                      <Pencil size={14} />
                    </button>
                    {deleteConfirm === cred.id ? (
                      <div className="md-delete-confirm">
                        <button className="md-action-btn danger" onClick={() => handleDelete(cred.id)} title="Confirm">
                          <Trash2 size={14} />
                        </button>
                        <button className="md-action-btn" onClick={() => setDeleteConfirm(null)} title="Cancel">
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <button className="md-action-btn danger" onClick={() => setDeleteConfirm(cred.id)} title="Delete">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
                {cred.description && <p className="md-cred-card-desc">{cred.description}</p>}
                <div className="md-cred-card-fields">
                  {config.fields.map((field) => {
                    const value = cred[field];
                    if (!value) return null;
                    const isSensitive = SENSITIVE_FIELDS.includes(field);
                    const visKey = `${cred.id}-${field}`;
                    const isVisible = showPasswords[visKey];
                    return (
                      <div key={field} className="md-cred-field">
                        <span className="md-cred-field-label">{FIELD_LABELS[field]}</span>
                        <span className="md-cred-field-value">
                          {isSensitive && !isVisible ? '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022' : value}
                          {isSensitive && (
                            <button className="md-eye-btn" onClick={() => togglePasswordVisibility(cred.id, field)}>
                              {isVisible ? <EyeOff size={12} /> : <Eye size={12} />}
                            </button>
                          )}
                        </span>
                      </div>
                    );
                  })}
                  {cred.priority > 0 && (
                    <div className="md-cred-field">
                      <span className="md-cred-field-label">Priority</span>
                      <span className="md-cred-field-value">{cred.priority}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content md-cred-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editId ? 'Edit Credential' : 'Add Credential'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="md-cred-proto-grid">
                {Object.entries(PROTOCOL_CONFIG).map(([key, config]) => (
                  <button
                    key={key}
                    className={`md-cred-proto-btn ${form.protocol === key ? 'active' : ''}`}
                    onClick={() => setForm(prev => ({ ...prev, protocol: key }))}
                    style={form.protocol === key ? { borderColor: config.color, background: config.color + '12' } : {}}
                  >
                    <span style={{ color: config.color }}>{config.icon}</span>
                    <span className="md-cred-proto-label">{config.label}</span>
                  </button>
                ))}
              </div>
              <p className="md-cred-proto-desc">{protocolConfig.description}</p>

              {form.protocol === 'WMI' && (
                <div className="form-group">
                  <label className="form-label">Account Type</label>
                  <div className="md-cred-subtype-row">
                    <label className="md-cred-radio">
                      <input type="radio" name="wmiSubtype" value="WMI_DOMAIN"
                        checked={form.credentialSubtype === 'WMI_DOMAIN'}
                        onChange={() => setForm(prev => ({ ...prev, credentialSubtype: 'WMI_DOMAIN' }))} />
                      <span>Domain</span>
                    </label>
                    <label className="md-cred-radio">
                      <input type="radio" name="wmiSubtype" value="WMI_LOCAL"
                        checked={form.credentialSubtype === 'WMI_LOCAL'}
                        onChange={() => setForm(prev => ({ ...prev, credentialSubtype: 'WMI_LOCAL', domain: '' }))} />
                      <span>Local</span>
                    </label>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Label *</label>
                <input className="form-input" placeholder="e.g. Camera Admin, SNMP Public"
                  value={form.label} onChange={(e) => setForm(prev => ({ ...prev, label: e.target.value }))} />
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <input className="form-input" placeholder="Optional description"
                  value={form.description} onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))} />
              </div>

              {protocolConfig.fields.map((field) => {
                // Hide domain field when WMI subtype is explicitly LOCAL
                if (form.protocol === 'WMI' && field === 'domain' && form.credentialSubtype === 'WMI_LOCAL') return null;
                const isSensitive = SENSITIVE_FIELDS.includes(field);
                const isSelect = field === 'authProtocol' || field === 'privProtocol';
                if (isSelect) {
                  const options = field === 'authProtocol' ? ['', 'MD5', 'SHA', 'SHA256'] : ['', 'DES', 'AES', 'AES256'];
                  return (
                    <div key={field} className="form-group">
                      <label className="form-label">{FIELD_LABELS[field]}</label>
                      <select className="form-select" value={form[field]}
                        onChange={(e) => setForm(prev => ({ ...prev, [field]: e.target.value }))}>
                        {options.map(opt => <option key={opt} value={opt}>{opt || '\u2014 Select \u2014'}</option>)}
                      </select>
                    </div>
                  );
                }
                return (
                  <div key={field} className="form-group">
                    <label className="form-label">{FIELD_LABELS[field]}</label>
                    <input className="form-input" type={isSensitive ? 'password' : 'text'}
                      placeholder={editId && isSensitive ? 'Leave blank to keep current' : `Enter ${FIELD_LABELS[field].toLowerCase()}`}
                      value={form[field]} onChange={(e) => setForm(prev => ({ ...prev, [field]: e.target.value }))}
                      autoComplete="off" />
                  </div>
                );
              })}

              <div className="form-group">
                <label className="form-label">Priority (lower = tried first)</label>
                <input className="form-input" type="number" min="0" max="99"
                  value={form.priority} onChange={(e) => setForm(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))} />
              </div>

              <div className="form-group">
                <label className="md-cred-checkbox">
                  <input type="checkbox" checked={!!form.isDefault}
                    onChange={(e) => setForm(prev => ({ ...prev, isDefault: e.target.checked }))} />
                  <Star size={14} /> Set as default for {PROTOCOL_CONFIG[form.protocol]?.label || form.protocol}
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <><Loader2 size={16} className="spin" /> Saving...</> : <><Save size={16} /> {editId ? 'Update' : 'Save'}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {testModal && (
        <div className="modal-overlay" onClick={() => !testRunning && setTestModal(null)}>
          <div className="modal-content md-cred-test-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FlaskConical size={20} /> Test "{testModal.label}"</h2>
              <button className="modal-close" onClick={() => !testRunning && setTestModal(null)} disabled={testRunning}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Target IP Address</label>
                <input className="form-input" placeholder="e.g. 192.168.1.50"
                  value={testTargetIp}
                  onChange={(e) => setTestTargetIp(e.target.value)}
                  disabled={testRunning}
                  autoFocus />
                <p className="md-cred-hint">
                  The agent will attempt a WMI connection to this host using the selected credential.
                </p>
              </div>
              {testResult && (
                <div className={`md-cred-test-result ${testResult.success ? 'success' : 'error'}`}>
                  {testResult.success ? (
                    <><CheckCircle2 size={18} /> Connection successful</>
                  ) : (
                    <><XCircle size={18} /> {testResult.error}</>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setTestModal(null)} disabled={testRunning}>Close</button>
              <button className="btn btn-primary" onClick={runTest} disabled={testRunning || !testTargetIp.trim()}>
                {testRunning ? <><Loader2 size={16} className="spin" /> Testing...</> : <><FlaskConical size={16} /> Run Test</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPrompt && (
        <TutorialPrompt
          module={credentialsTutorial.module}
          moduleName={credentialsTutorial.moduleName}
          steps={credentialsTutorial.steps}
          onClose={dismissTutorial}
        />
      )}
    </div>
  );
};

export default MdCredentials;
